// Dependencias.
import Vue from 'vue';

// Estado inicial
const initialState = () => ({
  landingPages: [],
  total: 0,
  loadingState: false,
  creating: false,
  createError: null,
  activeTab: 'prod',
  searchQuery: '',
  lpDetails: null,
  lpDetailsLoading: false,
  lpDetailsError: null
});

// Objeto de estado
const state = initialState();

// Helper para detectar ambiente baseado no domínio
const detectEnvironment = (dominio) => {
  if (!dominio) return 'prod';
  return dominio.includes('.hlp.polgo.online') ? 'homolog' : 'prod';
};

// Getters
const getters = {
  getLandingPagesList(state) {
    return state.landingPages;
  },

  getFilteredLandingPages(state) {
    let filtered = state.landingPages.filter(lp => {
      // Detectar ambiente baseado no domínio
      const detectEnvironment = (domain) => {
        if (!domain) return null;
        return domain.includes('.hlp.polgo.online') ? 'homolog' : 'prod';
      };

      // Detectar ambiente da LP
      let lpEnvironment = detectEnvironment(lp.dominio);
      
      // Aplicar filtro de ambiente
      let environmentMatch = false;
      if (state.activeTab === 'homolog') {
        environmentMatch = lpEnvironment === 'homolog';
      } else if (state.activeTab === 'prod') {
        environmentMatch = lpEnvironment === 'prod';
      }
      
      // Filtrar por pesquisa
      const searchMatch = !state.searchQuery || 
        (lp.nome && lp.nome.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
        (lp.dominio && lp.dominio.toLowerCase().includes(state.searchQuery.toLowerCase()));
      
      return environmentMatch && searchMatch;
    });

    return filtered;
  },

  getTotal(state) {
    return state.total;
  },

  getLoadingState(state) {
    return state.loadingState;
  },

  getCreatingState(state) {
    return state.creating;
  },

  getCreateError(state) {
    return state.createError;
  },

  getActiveTab(state) {
    return state.activeTab;
  },

  getSearchQuery(state) {
    return state.searchQuery;
  },

  getLpDetails(state) {
    return state.lpDetails;
  },

  getLpDetailsLoading(state) {
    return state.lpDetailsLoading;
  },

  getLpDetailsError(state) {
    return state.lpDetailsError;
  },
};

// Ações
const actions = {
  clearState({ commit }) {
    commit('RESET');
  },

  setActiveTab({ commit }, tab) {
    commit('setActiveTab', tab);
  },

  setSearchQuery({ commit }, query) {
    commit('setSearchQuery', query);
  },

  finishCreating({ commit }) {
    commit('setCreating', false);
  },

  setCreating({ commit }, value) {
    commit('setCreating', value);
  },

  createLandingPage({ commit }, { dominio, ambiente, configuracao }) {
    return new Promise(async resolve => {

      console.log('configuracao', configuracao);

      // Validar e logar ambiente antes de enviar
      console.log('[STORE] Criando landing page:', { dominio, ambiente, configuracao: !!configuracao });
      
      if (!ambiente || !['homolog', 'prod'].includes(ambiente)) {
        console.error('[STORE] Ambiente inválido ou não informado:', ambiente);
        commit('setCreating', false);
        commit('setCreateError', {
          status: 400,
          message: 'Ambiente inválido. Deve ser "homolog" ou "prod"'
        });
        resolve({ status: 400, msg: 'Ambiente inválido' });
        return;
      }
      
      const api = new Vue.prototype.$httpClient();
      const url = `/landingpages/criar`;
      commit('setCreating', true);
      
      const requestData = {
        dominio: dominio,
        ambiente: ambiente
      };
      
      console.log('[STORE] Dados enviados para API:', requestData);
      
      // Adicionar configuração se fornecida
      if (configuracao) {
        requestData.configuracao = configuracao;
      }
      
      const response = await api.send(
        url,
        'POST',
        requestData,
        undefined,
        false
      );
      
      if (response.status == 200) {
        // Definir creating como false para permitir que o modal avance automaticamente
        commit('setCreating', false);
        resolve({
          status: 200,
          msg: 'Landing page criada com sucesso.',
          data: response.data?.dados || response.data || null // Incluir dados da resposta
        });
      } else {
        // Só define como false em caso de erro
        commit('setCreating', false);
        commit('setCreateError', {
          status: response.status,
          message: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
          details: response.data
        });
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  fetchLandingPages({ commit }) {
    return new Promise(async resolve => {
      commit('setLandingPages', []);
      commit('setTotal', 0);

      const api = new Vue.prototype.$httpClient();
      const url = `/landingpages/listar`;
      
      commit('setLoadingState', true);
      const response = await api.send(url, 'GET', null, undefined, false);
      commit('setLoadingState', false);

      if (response.status == 200) {
        const data = response.data.dados || {};
        const landingPages = data.landingPages || [];
        commit('setLandingPages', landingPages);
        commit('setTotal', data.total || landingPages.length);

        resolve({
          status: 200,
          msg: 'Lista de landing pages recuperada.',
        });
      } else {
        commit('setLandingPages', []);
        commit('setTotal', 0);

        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro desconhecido durante sua requisição.',
        });
      }
    });
  },

  async fetchLpDetails({ commit }, dominio) {
    commit('setLpDetailsLoading', true);
    commit('setLpDetailsError', null);
    commit('setLpDetails', null);
    try {
      const api = new Vue.prototype.$httpClient();
      const url = `/landingpages/obter/${dominio}`;
      const response = await api.send(url, 'GET');
      if (response.status === 200 && response.data && response.data.dados) {
        commit('setLpDetails', response.data.dados);
      } else {
        commit('setLpDetailsError', response.data?.mensagem || 'Erro ao buscar detalhes da LP.');
      }
    } catch (e) {
      commit('setLpDetailsError', e.message || 'Erro inesperado ao buscar detalhes da LP.');
    } finally {
      commit('setLpDetailsLoading', false);
    }
  },

  async fetchLpConfiguration({ commit }, { dominio, ambiente }) {
    commit('setLpDetailsLoading', true);
    commit('setLpDetailsError', null);
    commit('setLpDetails', null);
    
    try {
      const api = new Vue.prototype.$httpClient();
      const url = `/landingpages/configuracao/${dominio}?ambiente=${ambiente}`;
      const response = await api.send(url, 'GET');
      
      if (response.status === 200 && response.data?.dados) {
        commit('setLpDetails', response.data.dados);
        return { status: 200, dados: response.data.dados };
      } else {
        const mensagem = response.data?.mensagem || 'Erro ao buscar configuração';
        commit('setLpDetailsError', mensagem);
        return { status: response.status, mensagem };
      }
    } catch (error) {
      const mensagem = error.message || 'Erro ao buscar configuração';
      commit('setLpDetailsError', mensagem);
      return { status: 500, mensagem };
    } finally {
      commit('setLpDetailsLoading', false);
    }
  },

  updateLandingPage({ commit }, { dominio, configuracao }) {
    return new Promise(async resolve => {
      console.log('[STORE] Atualizando landing page:', { dominio, configuracao: !!configuracao });
      
      const ambiente = detectEnvironment(dominio);
      console.log('[STORE] Editando LP com ambiente:', ambiente);
      
      const api = new Vue.prototype.$httpClient();
      const url = `/landingpages/editar/${dominio}?ambiente=${ambiente}`;
      
      commit('setCreating', true);
      
      const requestData = {
        configuracao: configuracao
      };
      
      const response = await api.send(
        url,
        'PUT',
        requestData,
        undefined,
        false
      );
      
      if (response.status == 200) {
        commit('setCreating', false);
        resolve({
          status: 200,
          msg: 'Landing page atualizada com sucesso.',
          data: response.data?.dados || response.data || null
        });
      } else {
        commit('setCreating', false);
        commit('setCreateError', {
          status: response.status,
          message: response.data.mensagem || 'Ocorreu um erro ao atualizar a landing page.',
          details: response.data
        });
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Ocorreu um erro ao atualizar a landing page.',
        });
      }
    });
  },

  deleteLandingPage({ commit }, dominio) {
    return new Promise(async resolve => {
      console.log('[STORE] Excluindo landing page:', dominio);
      
      const ambiente = detectEnvironment(dominio);
      console.log('[STORE] Excluindo LP com ambiente:', ambiente);
      
      const api = new Vue.prototype.$httpClient();
      const url = `/landingpages/excluir/${dominio}?ambiente=${ambiente}`;
      
      const response = await api.send(url, 'DELETE', null, undefined, false);
      
      if (response.status == 200) {
        // Verificar se há etapas com erro
        const etapas = response.data?.dados?.etapas || [];
        const hasError = etapas.some(etapa => etapa.status === 'ERRO');
        
        if (hasError) {
          // Exclusão parcial - houve erros em algumas etapas
          const erros = etapas.filter(e => e.status === 'ERRO').map(e => `${e.etapa}: ${e.mensagem}`).join(', ');
          console.warn('[STORE] Exclusão parcial com erros:', erros);
          resolve({
            status: 207, // Multi-Status
            msg: 'Exclusão parcial. Alguns recursos não foram removidos.',
            data: response.data?.dados,
            partial: true
          });
        } else {
          // Exclusão completa - todas as etapas OK ou AVISO
          console.log('[STORE] Exclusão completa realizada com sucesso');
          resolve({
            status: 200,
            msg: response.data?.mensagem || 'Landing page excluída com sucesso.',
            data: response.data?.dados
          });
        }
      } else {
        resolve({
          status: response.status,
          msg: response.data.mensagem || 'Erro ao excluir landing page.',
        });
      }
    });
  },

  async monitorarStatus() {
    const api = new Vue.prototype.$httpClient();
    const url = `/landingpages/monitorar-status`;
    const response = await api.send(url, 'POST', { ambiente: 'prod' }, undefined, false);
    if (response.status === 200) {
      return { status: 200, msg: response.data?.mensagem || 'Monitoramento concluído com sucesso.', data: response.data?.dados || response.data };
    }
    return { status: response.status, msg: response.data?.mensagem || 'Erro ao monitorar status.' };
  },

  async deleteLandingPages({ dispatch }, dominios) {
    console.log('[STORE deleteLandingPages] Starting bulk delete for', dominios.length, 'LPs');
    console.log('[STORE deleteLandingPages] Dominios:', dominios);
    
    const results = {
      success: [],
      partial: [],
      failed: []
    };
    
    // Excluir uma por vez (não em paralelo)
    for (let i = 0; i < dominios.length; i++) {
      const dominio = dominios[i];
      console.log(`[STORE deleteLandingPages] Deleting ${i + 1}/${dominios.length}: ${dominio}`);
      
      try {
        const result = await dispatch('deleteLandingPage', dominio);
        console.log(`[STORE deleteLandingPages] Result for ${dominio}:`, result);
        
        if (result.status === 200) {
          results.success.push(dominio);
          console.log(`[STORE deleteLandingPages] ✓ Success: ${dominio}`);
        } else if (result.status === 207 && result.partial) {
          results.partial.push({ dominio, data: result.data });
          console.log(`[STORE deleteLandingPages] ⚠ Partial: ${dominio}`);
        } else {
          results.failed.push({ dominio, error: result.msg });
          console.log(`[STORE deleteLandingPages] ✗ Failed: ${dominio} - ${result.msg}`);
        }
      } catch (error) {
        results.failed.push({ dominio, error: error.message });
        console.log(`[STORE deleteLandingPages] ✗ Error: ${dominio} - ${error.message}`);
      }
    }
    
    console.log('[STORE deleteLandingPages] Final results:', results);
    return results;
  },
};

// Mutações
const mutations = {
  RESET(state) {
    const newState = initialState();
    Object.keys(newState).forEach(key => {
      state[key] = newState[key];
    });
  },

  setLandingPages(state, data) {
    state.landingPages = data;
  },

  setTotal(state, data) {
    state.total = data;
  },

  setLoadingState(state, data) {
    state.loadingState = data;
  },

  setCreating(state, data) {
    state.creating = data;
    // Limpar erro quando iniciar nova criação
    if (data === true) {
      state.createError = null;
    }
  },

  setCreateError(state, error) {
    state.createError = error;
  },

  setActiveTab(state, data) {
    state.activeTab = data;
  },

  setSearchQuery(state, data) {
    state.searchQuery = data;
  },

  setLpDetails(state, data) {
    state.lpDetails = data;
  },

  setLpDetailsLoading(state, data) {
    state.lpDetailsLoading = data;
  },

  setLpDetailsError(state, data) {
    state.lpDetailsError = data;
  },

  removeLandingPage(state, dominio) {
    state.landingPages = state.landingPages.filter(lp => lp.dominio !== dominio);
    state.total = state.landingPages.length;
  },
};

// Exportando módulo
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}; 